import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { AdminRoute } from '@/components/auth/admin-route'
import { AuthProvider } from '@/providers/auth-provider'
import { PWAInstallBanner } from '@/components/pwa-install-banner'
import { OfflineIndicator } from '@/components/offline-indicator'
import { MobileTestHelper } from '@/components/mobile-test-helper'
import { CookieConsent } from '@/components/cookie-consent'
import { PageLoader } from '@/components/ui/page-loader'

// Public pages — eager loaded (critical path)
import { LandingPage } from '@/pages/landing'
import { LoginPage } from '@/pages/auth/login'
import { RegisterPage } from '@/pages/auth/register'
import { ForgotPasswordPage } from '@/pages/auth/forgot-password'
import { ResetPasswordPage } from '@/pages/auth/reset-password'
import { VerifyEmailPage } from '@/pages/auth/verify-email'
import { MagicLinkPage } from '@/pages/auth/magic-link'
import { MFAChallengePage } from '@/pages/auth/mfa-challenge'
import { PublicWaitlistPage } from '@/pages/public/waitlist'
import { OAuthCallback } from '@/components/auth/oauth-callback'
import { NotFoundPage } from '@/pages/not-found'
import Terms from '@/pages/terms'
import Privacy from '@/pages/privacy'
import RefundPolicy from '@/pages/refund-policy'

// Protected pages — lazy loaded (only needed post-auth)
const DashboardLayout = lazy(() =>
  import('@/components/layout/dashboard-layout').then(m => ({ default: m.DashboardLayout }))
)
const DashboardPage = lazy(() =>
  import('@/pages/dashboard').then(m => ({ default: m.DashboardPage }))
)
const WaitlistsPage = lazy(() =>
  import('@/pages/waitlists/index').then(m => ({ default: m.WaitlistsPage }))
)
const WaitlistDetailsPage = lazy(() =>
  import('@/pages/waitlists/details').then(m => ({ default: m.WaitlistDetailsPage }))
)
const ContentEditorPage = lazy(() =>
  import('@/pages/waitlists/content-editor').then(m => ({ default: m.ContentEditorPage }))
)
const AnalyticsPage = lazy(() =>
  import('@/pages/analytics').then(m => ({ default: m.AnalyticsPage }))
)
const SettingsPage = lazy(() =>
  import('@/pages/settings').then(m => ({ default: m.SettingsPage }))
)
const BillingPage = lazy(() =>
  import('@/pages/billing').then(m => ({ default: m.BillingPage }))
)
const BillingSuccessPage = lazy(() =>
  import('@/pages/billing/success').then(m => ({ default: m.BillingSuccessPage }))
)
const BillingCancelPage = lazy(() =>
  import('@/pages/billing/cancel').then(m => ({ default: m.BillingCancelPage }))
)

// Admin pages — lazy loaded (superadmin only)
const AdminDashboard = lazy(() => import('@/pages/dashboard/admin'))
const AdminCustomers = lazy(() => import('@/pages/dashboard/admin/customers'))
const AdminSubscriptions = lazy(() => import('@/pages/dashboard/admin/subscriptions'))
const AdminCustomerDetails = lazy(() => import('@/pages/dashboard/admin/customer-details'))
const AdminAuditLogs = lazy(() => import('@/pages/dashboard/admin/audit-logs'))

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PWAInstallBanner />
        <OfflineIndicator />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/magic-link" element={<MagicLinkPage />} />
          <Route path="/auth/mfa-challenge" element={<MFAChallengePage />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/w/:slug" element={<PublicWaitlistPage />} />

          {/* Legal pages */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />

          {/* Billing redirect pages */}
          <Route
            path="/billing/success"
            element={
              <Suspense fallback={<PageLoader />}>
                <BillingSuccessPage />
              </Suspense>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <Suspense fallback={<PageLoader />}>
                <BillingCancelPage />
              </Suspense>
            }
          />

          {/* Protected dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <DashboardLayout />
                </Suspense>
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="waitlists" element={<WaitlistsPage />} />
            <Route path="waitlists/:id" element={<WaitlistDetailsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<BillingPage />} />

            {/* Admin routes - superadmin only */}
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="admin/customers"
              element={
                <AdminRoute>
                  <AdminCustomers />
                </AdminRoute>
              }
            />
            <Route
              path="admin/subscriptions"
              element={
                <AdminRoute>
                  <AdminSubscriptions />
                </AdminRoute>
              }
            />
            <Route
              path="admin/customers/:userId"
              element={
                <AdminRoute>
                  <AdminCustomerDetails />
                </AdminRoute>
              }
            />
            <Route
              path="admin/audit-logs"
              element={
                <AdminRoute>
                  <AdminAuditLogs />
                </AdminRoute>
              }
            />
          </Route>

          {/* Content editor - standalone route outside dashboard layout */}
          <Route
            path="/waitlists/:id/content"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ContentEditorPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Redirects and 404 */}
          <Route path="/app" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <MobileTestHelper />
        <CookieConsent />
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
